// Angular imports
import { AfterViewInit, Component, OnInit, ViewChild } from '@angular/core';

// Import services

// Modal system imports
import { BaseModalComponent } from '../../modal/base-modal';
import { ModalService } from '../../modal/modal.service';

// Import components
import { InputFieldComponent } from '../../components/input-field/input-field.component';

// Import types 
import {
    CalculationLineApi,
    CalculationMetaApi} from '../../types';
import { Store } from '@ngrx/store';
import { AppState } from 'src/app/state-management/app-state';
import { selectCalculationMeta, selectLines, selectTemplate } from 'src/app/state-management/calculation-selectors';
import { Subject, takeUntil } from 'rxjs';
import { LineConfig } from 'src/app/stabu';

// Declare types
type TemplateSubcategory = {
    code: string;
    name: string;
    standardLines?: LineConfig[];
    textLinesOnly?: boolean;
}

type TemplateCategory = {
    code: string;
    name: string;
    open: boolean;
    subcategories: TemplateSubcategory[];
}

export type AddTemplateRowModalArgs = {
    addRows: (lines: CalculationLineApi[]) => void;
}

@Component({
    selector: 'add-stabu-row-modal',
    templateUrl: './add-stabu-row-modal.component.html',
    styleUrls: ['./add-stabu-row-modal.component.scss'],
    standalone: false
})
export class AddStabuRowModalComponent extends BaseModalComponent implements OnInit, AfterViewInit {
    private unsubscribe$ = new Subject<void>();

    // Modal system properties
    public title = 'Regel toevoegen';

    // Modal properties
    @ViewChild('codeField') public _codeField: InputFieldComponent;

    public _selectedCategories: TemplateCategory[] = [];
    public _selectedSubcategories: TemplateSubcategory[] = [];

    public calculationLines: CalculationLineApi[] = [];
    public meta: CalculationMetaApi = undefined;

    public _canAdd: boolean = false;

    public _code: string = '';
    public _description: string = '';

    public _template: TemplateCategory[];
    public _filteredTemplate: TemplateCategory[] = [];
    public _openCategories: string[] = [];

    public _useStandardLines: boolean = true;

    private addRowsCallback: (lines: CalculationLineApi[]) => void;

    constructor(private modalService: ModalService,
        private store: Store<AppState>) {
        super();

        // Fill template when it changes
        this.store.select(selectTemplate).pipe(
            takeUntil(this.unsubscribe$)
        ).subscribe(template => {
            if(!template){
                return;
            }
            // Load stabu
            this._template = template.map(cat => ({
                code: cat.categoryCode,
                name: cat.categoryName,
                open: false,
                subcategories: cat.subcategories.map<TemplateSubcategory>(subCat => {
                    return {
                        code: subCat.subcategoryCode,
                        name: subCat.subcategoryName,
                        standardLines: subCat.standardLines,
                        textLinesOnly: subCat.textLinesOnly
                    }
                })
            }));

            // Sort the stabu list
            this._template.sort((a, b) => Number(a.code) - Number(b.code));
        })

        this.store.select(selectLines).pipe(
            takeUntil(this.unsubscribe$)
        ).subscribe(lines => {
            this.calculationLines = lines;
        })

        this.store.select(selectCalculationMeta).pipe(
            takeUntil(this.unsubscribe$)
        ).subscribe(meta => {
            this.meta = meta;
        })

    }

    public ngOnInit(): void {
        this.clear();
    }

    public ngAfterViewInit(): void {
        // Set focus to prevend typing in the underlying calculation
        this._codeField?._inputElement?.nativeElement?.focus()
    }

    public init(args: AddTemplateRowModalArgs) {
        // Assign add rows
        this.addRowsCallback = args.addRows;
    }

    public clear() {
        this._code = '';
        this._description = '';
        this.closeAll();
        this.filterStabu('', ''); // No filter

        this._selectedCategories = [];
        this._selectedSubcategories = [];
    }

    public close() {
        // Close the modal
        this.modalService.close();
    }

    private closeAll() {
        this._template.forEach(cat => cat.open = false);
        this._filteredTemplate.forEach(cat => cat.open = false);
    }

    private filterStabu(code: string, name: string) {
        if (code != null) {
            let catCode = code.split('.')[0];
            if (catCode.length == 1 && code.includes('.'))
                catCode = '0' + catCode; // Prepend 0 if a . is present

            this._filteredTemplate = this._template.filter(cat => cat.code.includes(catCode));

            // More than category code given
            if (code.length > 3) {
                this._filteredTemplate = this._filteredTemplate.map(cat => {
                    const catCopy: TemplateCategory = JSON.parse(JSON.stringify(cat));
                    catCopy.subcategories = cat.subcategories.filter(sub => sub.code.includes(code));
                    catCopy.open = true;

                    return catCopy;
                }).filter(cat => cat.subcategories.length > 0);
            }
        } else if (name) {
            this._filteredTemplate = this._template.filter(cat => {
                const filteredSubs = cat.subcategories.filter(sub => sub.name.toLocaleLowerCase().includes(name.toLowerCase()));

                return filteredSubs.length > 0 || (filteredSubs.length == 0 && cat.name.toLowerCase().includes(name.toLowerCase()));
            }).map(cat => {
                const catCopy: TemplateCategory = JSON.parse(JSON.stringify(cat));
                // Filter out the subs in the copy (otherwise we modify the original) if the name of the category does not match with the name we're searching for
                catCopy.subcategories = cat.name.toLocaleLowerCase().includes(name.toLowerCase()) ? cat.subcategories : cat.subcategories.filter(sub => sub.name.toLocaleLowerCase().includes(name.toLowerCase()));
                catCopy.open = true;

                return catCopy;
            });
        }

        this._filteredTemplate.sort((a, b) => Number(a.code) - Number(b.code));

        if (this._filteredTemplate.length == 1) {
            this._filteredTemplate[0].open = true;

            if (this._filteredTemplate[0].subcategories.length == 1 && name == null) {
                this._code = this._filteredTemplate[0].subcategories[0].code;
                this._description = this._filteredTemplate[0].subcategories[0].name;
                this._canAdd = true;
            }
        }
    }

    /* --- Event handlers --- */

    public _onCategorySelect(category: TemplateCategory) {
        if (this._selectedCategories.includes(category)) {
            // Remove cat
            this._selectedCategories.splice(this._selectedCategories.indexOf(category), 1);

            // Remove subs as well
            this._selectedSubcategories = [...this._selectedSubcategories.filter(s => !category.subcategories.includes(s))];
        } else {
            // Add category
            this._selectedCategories = [...this._selectedCategories, category];

            // Add subcategories
            this._selectedSubcategories = [...this._selectedSubcategories, ...category.subcategories.filter(s => !this._selectedSubcategories.includes(s))];
        }

        // Update can add
        this._canAdd = this._selectedSubcategories.length > 0;
    }

    public _onSubcategorySelect(category: TemplateCategory, subcategory: TemplateSubcategory) {
        if (this._selectedSubcategories.includes(subcategory)) {
            // Unselect the subcategory
            this._selectedSubcategories.splice(this._selectedSubcategories.indexOf(subcategory), 1);
            this._selectedSubcategories = [...this._selectedSubcategories];

            // Reset categroy selection
            if (this._selectedCategories.includes(category)) {
                // Remove cat
                this._selectedCategories.splice(this._selectedCategories.indexOf(category), 1);
                this._selectedCategories = [...this._selectedCategories];
            }
        } else {
            // Select the subcategory
            this._selectedSubcategories = [...this._selectedSubcategories, subcategory];

            // Also toggle category checkbox if every sub is selected
            const categoryChecked = category.subcategories.find(s => !this._selectedSubcategories.includes(s)) == undefined;
            if (categoryChecked)
                this._selectedCategories = [...this._selectedCategories, category];
        }

        // Update can add
        this._canAdd = this._selectedSubcategories.length > 0;
    }

    public _onKeydownCode(event: KeyboardEvent) {
        if (event.key != 'Enter' && event.key != 'ArrowUp' && event.key != 'ArrowDown') return;

        // Prevent default behavior
        event.preventDefault();

        if (event.key == 'ArrowDown' || event.key == 'ArrowUp') {
            if (this._code.indexOf('.') == -1) {
                const index = this.scrollIndex(this._template.findIndex((val) => val.code == this._code), event.key == 'ArrowDown', this._template.length - 1);

                const cat = this._template[index];
                this._code = cat.code;
                this._description = cat.name;

                this.filterStabu(cat.code, null);
            } else {
                const catCode = this._code.split('.')[0];
                const cat = this._template.find(cat => cat.code == catCode);
                if (!cat) return;

                const index = this.scrollIndex(cat.subcategories.findIndex(val => val.code == this._code), event.key == 'ArrowDown', cat.subcategories.length - 1);

                const sub = cat.subcategories[index];
                this._code = sub.code;
                this._description = sub.name;
                this._canAdd = true;
            }
        } else if (event.key == 'Enter') {
            if (this._template.find(val => val.code == this._code))
                this._code += '.';
            if (this._canAdd)
                this._addRow();
        }
    }

    public _onCodeChange(value: string) {
        this._code = value;

        this.closeAll();
        this.filterStabu(this._code, null);

        if (this._filteredTemplate.length != 1 || (this._filteredTemplate.length == 1 && this._filteredTemplate[0].subcategories.length != 1)) {
            this._description = '';
            this._canAdd = false;
        }
    }

    public _onDescriptionChange(value: string) {
        this._description = value.trim();

        this.closeAll();

        if (this._description.length != 0)
            this.filterStabu(null, this._description);
        else {
            this.closeAll();
            this.filterStabu('', '');
        }

        if (this._filteredTemplate.length != 1 || (this._filteredTemplate.length == 1 && this._filteredTemplate[0].subcategories.length != 1)) {
            this._code = '';
            this._canAdd = false;
        }
    }

    public _onClickCategory(cat: TemplateCategory) {
        this._code = `${cat.code}.`;
        cat.open = true;

        this.filterStabu(cat.code, null);
    }

    public _onClickSubCategory(sub: TemplateSubcategory) {
        this._code = sub.code;
        this._description = sub.name;
        this._canAdd = true;

        this.filterStabu(sub.code, null);
    }

    public _onClickOpenCategory(cat: TemplateCategory, event: Event) {
        cat.open = !cat.open;

        event.stopPropagation();
    }

    /* --- Helpers --- */

    private scrollIndex(index: number, scrollDown: boolean, max: number): number {
        if (scrollDown)
            index += 1;
        else
            index -= 1;

        return Math.max(0, Math.min(max, index));
    }

    public _addRow() {
        if (!this._canAdd) return;

        // Disable can add for the future
        this._canAdd = false;

        // Trigger add rows callback
        this.addRowsCallback(this._selectedSubcategories.map(s => {
            // Split code
            const [catCode, subcatCode] = s.code.split('.');

            // Get the stabu subcategory config
            // 20230915 Will be filled from template provided by backend
            // No longer part of front end logic
            const config = this._template.find(cat => cat.code === catCode)?.subcategories.find(sub => sub.code === s.code);

            if (!!config?.standardLines && this._useStandardLines) {
                // Lines will be added at the end of the subcategory
                // So we need the current maximal sort order
                const maxSortOrder = this.calculationLines.filter(l => l.code === [catCode, subcatCode].join('.')).reduce<number>((p, c) => c.sortOrder > p ? c.sortOrder : p, 0) ?? 0;
                // console.log('MAX SORT', maxSortOrder);

                return config.standardLines!.map<Partial<CalculationLineApi>>((lc, i) => ({
                    code: s.code,
                    description: lc.description,
                    quantity: lc.quantity?.toString(),
                    unit: lc.unit,
                    defaultPercentages: lc.type === 'calculation' ? this.meta?.percentages : undefined,
                    isTextLine: lc.type !== 'calculation' || !lc.unit,
                    sortOrder: (i + 1) * 100 + maxSortOrder
                }));
            } else {
                return { code: s.code, unit: 'stk', defaultPercentages: !config.textLinesOnly ? this.meta?.percentages : undefined, isTextLine: !!config.textLinesOnly }
            }
        }).reduce((insert, value) => {
            if (Array.isArray(value))
                insert.push(...value);
            else insert.push(value);

            return insert;
        }, [] as any[]));

        // Close the modal
        this.close();
    }
}
